import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SlidersHorizontal } from 'lucide-react';
import { get } from '@/utils/apiClient';
import AIInteractionPreferencesTab from '@/features/my-settings/AIInteractionPreferencesTab';

const RESPONSE_LABELS: Record<string, string> = {
  supportive: 'Supportive',
  balanced: 'Balanced',
  challenging: 'Challenging',
};

const COMMUNICATION_LABELS: Record<string, string> = {
  warm: 'Warm',
  balanced: 'Balanced',
  direct: 'Direct',
};

interface PreferenceSummary {
  response_style: string;
  communication_style: string;
}

/**
 * Compact pill shown on AI chat surfaces. Displays the clinician's current
 * Interaction Preferences (e.g. "Supportive · Warm") and opens the canonical
 * AI Preferences editor in a modal on click. The embedded editor is the same
 * component as My Settings → AI Preferences, so saving applies to the same
 * GET/PATCH /api/user-ai-preferences contract.
 */
export default function AIPreferencesPill() {
  const [summary, setSummary] = useState<PreferenceSummary | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const response = await get('/api/user-ai-preferences');
      if (!response.ok) {
        setSummary(null);
        return;
      }
      const data = (await response.json()) as Partial<PreferenceSummary>;
      setSummary({
        response_style: data.response_style ?? 'balanced',
        communication_style: data.communication_style ?? 'balanced',
      });
    } catch {
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleClose = () => {
    setOpen(false);
    void refresh();
  };

  if (!summary) return null;

  const label = `${RESPONSE_LABELS[summary.response_style] ?? 'Balanced'} · ${
    COMMUNICATION_LABELS[summary.communication_style] ?? 'Balanced'
  }`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`AI preferences: ${label}. Click to adjust.`}
        className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
      >
        <SlidersHorizontal className="w-3 h-3" />
        {label}
      </button>
      {open && (
        <Dialog open onClose={handleClose} fullWidth maxWidth="md">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            AI Preferences
            <IconButton aria-label="Close" onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <AIInteractionPreferencesTab embedded onSaved={handleClose} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
